import adminAuthApi from '../Config/adminAuthApi'
import {
  AdminMetadata,
  AdminUser,
  AdminUserListParams,
  CreateAdminUserRequest,
  CreateRoleRequest,
  CurrentAdmin,
  Platform,
  PlatformEnvironment,
  PlatformGrant,
  Role,
  ServerResponse,
  UpdateRoleRequest,
} from '../Models/AdminManagement'


const unwrap = <T>(body: ServerResponse<T>): T => body?.data

// Row count for pager from meta.pagination.total; returns null if absent for fallback paging.
export const readPaginationMeta = (meta: ServerResponse<unknown>['meta']): number | null => {
  const total = meta?.pagination?.total
  return typeof total === 'number' ? total : null
}

// Display metadata mapping for platforms and environments from the API

export const PLATFORM_DISPLAY: Record<
  Platform,
  { label: string; description?: string; color: string }
> = {
  tradegrid: {
    label: 'TradeGrid',
    description: 'Core B2B energy trading, order fulfillment, and finance portal',
    color: '#E873A8',
  },
  boostxpress: {
    label: 'BoostXpress',
    description: 'Fuel station attendant management, vouchers, POS & retail transactions',
    color: '#00C12B',
  },
  // Descriptions left blank rather than guessed fill in when confirmed.
  terminalone: { label: 'Terminal One', color: '#3B82F6' },
  analytics: { label: 'Analytics', color: '#F59E0B' },
  auth: { label: 'Admin Auth', color: '#8B5CF6' },
}

export const ENVIRONMENT_DISPLAY: Record<PlatformEnvironment, { label: string; color: string }> = {
  production: { label: 'Production', color: '#10B981' },
  staging: { label: 'Staging', color: '#F59E0B' },
  development: { label: 'Development', color: '#64748B' },
}

const stringToColor = (str: string): string => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  const c = (hash & 0x00ffffff).toString(16).toUpperCase()
  return '#' + '00000'.substring(0, 6 - c.length) + c
}

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)

export const platformLabel = (platform: string): string =>
  PLATFORM_DISPLAY[platform as Platform]?.label ?? capitalize(platform)

export const platformColor = (platform: string): string =>
  PLATFORM_DISPLAY[platform as Platform]?.color ?? stringToColor(platform)

export const platformDescription = (platform: string): string | undefined =>
  PLATFORM_DISPLAY[platform as Platform]?.description

export const environmentLabel = (environment: string): string =>
  ENVIRONMENT_DISPLAY[environment as PlatformEnvironment]?.label ?? capitalize(environment)

export const environmentColor = (environment: string): string =>
  ENVIRONMENT_DISPLAY[environment as PlatformEnvironment]?.color ?? stringToColor(environment)

// firstName/lastName are both optional on the API, so fall back to the email.
export const adminFullName = (
  admin: Pick<AdminUser, 'firstName' | 'lastName' | 'email'>,
): string => {
  const name = [admin.firstName, admin.lastName].filter(Boolean).join(' ').trim()
  return name || admin.email
}

// Status is a boolean on the API, not an enum. There is no 'Suspended'.
export const adminStatusLabel = (admin: Pick<AdminUser, 'isDisabled'>): 'Active' | 'Disabled' =>
  admin.isDisabled ? 'Disabled' : 'Active'

export const hasGrant = (
  grants: PlatformGrant[],
  platform: Platform,
  environment: PlatformEnvironment,
): boolean => grants.some((g) => g.platform === platform && g.environment === environment)

export const toggleGrant = (
  grants: PlatformGrant[],
  platform: Platform,
  environment: PlatformEnvironment,
): PlatformGrant[] =>
  hasGrant(grants, platform, environment)
    ? grants.filter((g) => !(g.platform === platform && g.environment === environment))
    : [...grants, { platform, environment }]

// Admin Management Service

type Id = number | string

const adminManagementService = {
  // The signed-in admin. Used to gate write actions on SUPER_ADMIN.
  getCurrentAdmin: async (): Promise<CurrentAdmin> => {
    const res = await adminAuthApi.get<ServerResponse<CurrentAdmin>>('/auth/me')
    return unwrap(res.data)
  },

  // Roles, platforms and environments in a single call.
  getMetadata: async (): Promise<AdminMetadata> => {
    const res = await adminAuthApi.get<ServerResponse<AdminMetadata>>('/admin/metadata')
    return unwrap(res.data)
  },

  /** Server-side paginated; returns rows and total for local pager UI. */
  getUsers: async (
    params: AdminUserListParams = {},
  ): Promise<{ users: AdminUser[]; total: number | null }> => {
    const query: Record<string, string> = {}
    if (params.page !== undefined) query.page = String(params.page)
    if (params.limit !== undefined) query.limit = String(params.limit)
    if (params.search) query.search = params.search
    if (params.disabled !== undefined) query.disabled = String(params.disabled)

    const res = await adminAuthApi.get<ServerResponse<AdminUser[]>>('/admin/users', {
      params: query,
    })

    return {
      users: unwrap(res.data) ?? [],
      total: readPaginationMeta(res.data?.meta),
    }
  },

  getUserById: async (id: Id): Promise<AdminUser> => {
    const res = await adminAuthApi.get<ServerResponse<AdminUser>>(`/admin/users/${id}`)
    return unwrap(res.data)
  },

  createUser: async (payload: CreateAdminUserRequest): Promise<AdminUser> => {
    const res = await adminAuthApi.post<ServerResponse<AdminUser>>('/admin/users', payload)
    return unwrap(res.data)
  },

  // Requires SUPER_ADMIN. There is no DELETE for users — disable instead.
  disableUser: async (id: Id): Promise<AdminUser> => {
    const res = await adminAuthApi.patch<ServerResponse<AdminUser>>(`/admin/users/${id}/disable`)
    return unwrap(res.data)
  },

  // Requires SUPER_ADMIN.
  enableUser: async (id: Id): Promise<AdminUser> => {
    const res = await adminAuthApi.patch<ServerResponse<AdminUser>>(`/admin/users/${id}/enable`)
    return unwrap(res.data)
  },

  // Requires SUPER_ADMIN. Replaces the user's whole role set.
  updateUserRoles: async (id: Id, roleNames: string[]): Promise<AdminUser> => {
    const res = await adminAuthApi.put<ServerResponse<AdminUser>>(`/admin/users/${id}/roles`, {
      roleNames,
    })
    return unwrap(res.data)
  },

  // Requires SUPER_ADMIN. Replaces the user's whole grant set (name/email not updatable).
  updateUserPlatforms: async (id: Id, platforms: PlatformGrant[]): Promise<AdminUser> => {
    const res = await adminAuthApi.put<ServerResponse<AdminUser>>(`/admin/users/${id}/platforms`, {
      platforms,
    })
    return unwrap(res.data)
  },

  // Roles

  getRoles: async (): Promise<Role[]> => {
    const res = await adminAuthApi.get<ServerResponse<Role[]>>('/admin/roles')
    return unwrap(res.data) ?? []
  },

  // These endpoints require SUPER_ADMIN.
  createRole: async (payload: CreateRoleRequest): Promise<Role> => {
    const res = await adminAuthApi.post<ServerResponse<Role>>('/admin/roles', payload)
    return unwrap(res.data)
  },

  updateRole: async (id: Id, payload: UpdateRoleRequest): Promise<Role> => {
    const res = await adminAuthApi.patch<ServerResponse<Role>>(`/admin/roles/${id}`, payload)
    return unwrap(res.data)
  },

  deleteRole: async (id: Id): Promise<void> => {
    await adminAuthApi.delete<ServerResponse<null>>(`/admin/roles/${id}`)
  },
}

export default adminManagementService
